import FontBody2 from "../fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../fonts/font-props";
import IconUpload from "../icons/icon-upload/icon-upload";
import "./file-upload.scss";
import useUuid from "../../hooks/uuid-hook";
import { isSet } from "../../models/model-types-methods";
import FontBody1 from "../fonts/font-body1/font-body1";
import RoundLoader, {
    RoundLoaderType,
} from "../loaders/round-loader/round-loader";

export enum FILE_UPLOAD_VARIANTS {
    Default = "default",
    Secondary = "secondary",
}

interface fileUploadProps {
    uploadFile: (
        fileData: File,
        fileContent: string | ArrayBuffer | null | undefined
    ) => void;
    acceptedFileFormats?: string;
    buttonText?: string;
    readType?: "text" | "dataUrl";
    fileTypes?: string[];
    disabled?: boolean;
    maxFileSize?: number;
    multiple?: boolean;
    variant?: FILE_UPLOAD_VARIANTS;
    isLoading?: boolean;
}

export interface IFileData {
    // Discuss during CR:
    // This is debatable. Keeping it as string is wrong. Fixing this increases risk and code changes.
    // The intention is to "fix" types that are passed around, IFileData.file_data is a type that is
    // broken throughout our code (it's treated as file_data as string across the places which silents
    // compiler where it's defined as file_data: string but it can be one of string | ArrayBuffer | null | undefined in reality.
    file_data: string | ArrayBuffer | null | undefined;
    mime_type: string;
    original_name: string;
    size: number;
}

const fileUploadDefaultPropFileTypes = ["text/csv"];

const FileUpload: React.FC<fileUploadProps> = ({
    buttonText = "Upload File",
    readType = "text",
    fileTypes = fileUploadDefaultPropFileTypes,
    disabled = false,
    multiple = false,
    isLoading = false,
    uploadFile,
    acceptedFileFormats,
    maxFileSize,
    variant = FILE_UPLOAD_VARIANTS.Default,
}) => {
    const uuid = useUuid();
    const elemId = `file-upload-${uuid}`;

    let ButtonText;
    /** Explanation: using ! here as TS doesn't recognize `!== undefined` check in isSet() */
    if ([FILE_UPLOAD_VARIANTS.Default].includes(variant!)) {
        ButtonText = <FontBody2>{buttonText}</FontBody2>;
    }
    if ([FILE_UPLOAD_VARIANTS.Secondary].includes(variant!)) {
        ButtonText = (
            <FontBody1
                color={FONT_COLOR.ACCENT1}
                variant={[FONT_VARIANT.BOLD, FONT_VARIANT.UPPERCASE]}
            >
                {buttonText}
            </FontBody1>
        );
    }

    const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const files = event.target.files;
            if (files) {
                Object.keys(files).forEach((key, index) => {
                    const file = files[index];
                    if (maxFileSize && file.size > maxFileSize) {
                        throw new Error(`File is too large.`);
                    }
                    /**
                     * Check uploaded file type eligibility, we may specify accepted types,
                     * or allow all file types by setting "*" in fileTypes prop
                     */
                    if (
                        !fileTypes?.includes(file.type) &&
                        !fileTypes?.includes("*")
                    ) {
                        throw new Error(
                            `File type isn't eligible. Please upload only a ${acceptedFileFormats} file.`
                        );
                    }

                    const reader = new FileReader();
                    reader.onload = (e) => {
                        uploadFile(file, e.target?.result);
                    };

                    if (readType === "text") {
                        reader.readAsText(file);
                    } else if (readType === "dataUrl") {
                        reader.readAsDataURL(file);
                    } else {
                        throw new Error("unsupported readType");
                    }
                });
                /** clears the input after uploading the files, just in case, probably due to some browsers behavior or taken from form submition */
                event.target.value = "";
            }
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div
            className={`file-upload-container ${variant} ${
                isLoading ? "is-loading" : ""
            }`}
        >
            <label
                htmlFor={elemId}
                className={`file-upload-label ${
                    disabled || isLoading ? "disabled" : ""
                }`}
            >
                {isLoading && (
                    <div className="loader-container">
                        <RoundLoader type={RoundLoaderType.Dark} />
                    </div>
                )}
                {ButtonText}
                <div className="icon-container">
                    <IconUpload />
                </div>
            </label>
            <input
                type="file"
                id={elemId}
                className="file-upload-input"
                accept={acceptedFileFormats}
                onChange={handleUploadFile}
                disabled={disabled || isLoading}
                multiple={multiple}
            />
        </div>
    );
};

export default FileUpload;
